import { useFormDataQuery } from '@/api/getters';
import { FormControl, FormField, FormItem } from '@/components/ui/Form';
import { InputMask } from '@/components/ui/InputMask';
import { OWNER_ACCOUNT_IS_OWNER_NAME } from '@/features/v2/summaryForms/components/CheckboxAccountIsOwner';
import { validateZipCode } from '@/features/v2/summaryForms/Validate';

import { useLingui } from '@lingui/react';
import { useFormContext } from 'react-hook-form';

export const OWNER_ZIP_CODE_NAME = 'owner.zip_code';
export const OWNER_ZIP_CODE_COMPANY_NAME = 'owner.zip_code_company';

const InputPostalCode = ({
  name = OWNER_ZIP_CODE_NAME,
  focused,
  setFocused,
  disabled,
  autoCompleteOff = false,
  prefix,
  controlled,
}: {
  name: string;
  focused: string;
  setFocused: (value: string) => void;
  disabled?: boolean;
  autoCompleteOff?: boolean;
  prefix?: string;
  controlled?: boolean;
}) => {
  const { i18n } = useLingui();
  const { trigger, setValue, watch } = useFormContext();
  const { data } = useFormDataQuery();

  const postalCode = watch(name);

  const resetAddress = () => {
    setValue(`${prefix}.city`, '');
    setValue(`${prefix}.address`, '');
    setValue(`${prefix}.without_street`, false);
  };

  const isAccountOwner =
    !!watch(OWNER_ACCOUNT_IS_OWNER_NAME) && name === OWNER_ZIP_CODE_NAME;

  return (
    <FormField
      name={name}
      rules={{
        validate: (value: string) => validateZipCode(value),
      }}
      defaultValue={isAccountOwner ? data?.user?.postal_code : ''}
      render={({ field, fieldState }) => {
        const { value, onChange, onBlur, ...restField } = field;
        return (
          <FormItem className="col-span-4 md:col-span-1">
            <FormControl>
              <InputMask
                mask="00-000"
                inputMode="numeric"
                value={value}
                autoComplete={autoCompleteOff ? 'none' : 'postal-code'}
                label={i18n.t('Kod pocztowy')}
                disabled={disabled}
                error={fieldState.error?.message}
                success={
                  focused !== field.name && !fieldState.error && field.value
                }
                onAccept={(e, _mask, event) => {
                  if (
                    (e ?? '').replace('-', '') ===
                    (postalCode ?? '').replace('-', '')
                  )
                    return;
                  if (controlled && event) {
                    resetAddress();
                  }
                  if (e === null) {
                    onChange('');
                    return;
                  }
                  if (e.length < value?.length) {
                    onChange(e);
                    return;
                  }
                  onChange(e);
                  if (fieldState.error) {
                    trigger(field.name);
                  }
                }}
                onBlur={() => {
                  trigger(field.name);
                  setFocused('');
                  onBlur();
                }}
                onFocus={() => setFocused(field.name)}
                data-testid={`postal-code-input${prefix ? `-${prefix}` : ''}`}
                {...restField}
              />
            </FormControl>
          </FormItem>
        );
      }}
    />
  );
};

export default InputPostalCode;
